import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  LOCALE_ID,
  OnInit,
  Signal,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { RecordsService } from './records.service';
import {
  closePopupData,
  ColumnSortEvent,
  createErrorPopupData,
  createSuccessPopupData,
  CustomLineSkeletonComponent,
  getColumnsWithWantedWidths,
  getTableColumnsFromTableRows,
  isUserPosWorklistRightPvmo,
  KeyValueLabel,
  LoaderComponent,
  LoadingButtonComponent,
  NoValue,
  NUMBER_ONLY_REGEX,
  PopinActionComponent,
  SelectShownValueSignalComponent,
  SelectSignalComponent,
  SortDirectionEnum,
  StatusMessagePopupV2Component,
  StatusPopupElement,
  TableActionEvent,
  TableColumn,
  TableData,
  TableRow,
  TableSubActionEvent,
  TableV2Component,
  TextIconSignalInputComponent,
  toKeyValueWithValueLabel,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface
} from '@tribeauto/ngx-mobilite-material';
import {
  catchError,
  debounceTime,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  iif,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError
} from 'rxjs';
import {
  baseUserRecordsPreferences,
  DEFAULT_FILTERS,
  MyFile,
  ReasonType,
  RecordsColumnsEnum,
  RecordsFilterData,
  RecordsRequest,
  RecordsResponse,
  RecordsSortProperty
} from './records.component.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { fileToRowData, rowDataToTableRow } from './records.component.mapper';
import { PageEvent } from '@angular/material/paginator';
import { AuthenticationService } from '../../authentication/authentication.service';
import { ReferenceService } from '../../reference/reference.service';
import { ReferenceStore } from '../../reference/reference-store.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ApplicationStore } from '../../../core/services/application-store.service';
import { UpperCasePipe } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  getWebStoreIds,
  pointOfSalesToKeyValueLabelsWithAllOption,
  posAll
} from '../../../core/helpers/point-of-sale.helper';
import { ActionButtonEnum } from '../contract-action/action-list.model';
import { ContractActionService } from '../contract-action/contract-action.service';
import { UserService } from '../../user-connection/user.service';
import { AUTO_CLOSE_DELAY_TEN_SECONDS } from '../../helpers/popup.helper';
import {
  areEqualsKeyValue,
  sellerAll,
  sellersToKeyValueLabelsWithAllOption
} from '../../../core/helpers/seller.helper';
import { initFiltersFormSignal } from '../../helpers/table.helper';
import { RecordsPreferencesComponent } from './records-preferences/records-preferences.component';
import { PreferencesService } from '../preferences/preferences.service';
import { UserTablePreferences } from '../preferences/preferences.model';
import { getReferenceValue } from '../../helpers/reference.helper';
import { ReferenceValue } from '../../reference/reference.model';
import { debounce, form, FormField, maxLength, minLength, validate } from '@angular/forms/signals';

const prefixRouteRecordsColumns = 'application.records.table.columns.';

@Component({
  selector: 'pvmo-records',
  imports: [
    ReactiveFormsModule,
    LoaderComponent,
    TextIconSignalInputComponent,
    UpperCasePipe,
    MatCheckbox,
    StatusMessagePopupV2Component,
    LoadingButtonComponent,
    SelectSignalComponent,
    SelectShownValueSignalComponent,
    CustomLineSkeletonComponent,
    TableV2Component,
    TranslatePipe,
    RecordsPreferencesComponent,
    PopinActionComponent,
    FormField
  ],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './records.component.html',
  styleUrl: './records.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class RecordsComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly referenceService = inject(ReferenceService);
  private readonly referenceStore = inject(ReferenceStore);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly locale = inject(LOCALE_ID);
  private readonly recordsService = inject(RecordsService);
  private readonly translateService = inject(TranslateService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly contractActionService = inject(ContractActionService);
  private readonly userService = inject(UserService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  readonly router = inject(Router);
  records: Signal<ElementRef> = viewChild.required('records');

  isUserConnected = this.userService.isUserConnected;

  index = input<number>();
  pos = input<string>();
  seller = input<string>();
  cckNumber = input<string>();
  clientName = input<string>();
  sortProperty = input<string>();
  sortDirection = input<SortDirectionEnum>();
  tveiListMap: KeyValueLabel[] = pointOfSalesToKeyValueLabelsWithAllOption(
    this.applicationStore.instant('userData')()?.pointOfSales,
    this.translateService
  );

  translatedReasonTypeChoiceMap = toKeyValueWithValueLabel(ReasonType, (value: string) =>
    this.translateService.instant('application.records.cancellation.' + value)
  );

  sellerListMap = signal<KeyValueLabel[]>([]);

  selectedSeller: WritableSignal<KeyValueLabel | null> = signal(null);
  pageEvent = new PageEvent();

  files = signal<TableRow[] | undefined>(undefined);
  loadedFiles: TableRow[] | null = null;
  readonly filters = signal<RecordsFilterData>(DEFAULT_FILTERS);

  readonly filterForm = form(this.filters, path => {
    debounce(path.cckNumber, 'blur');
    validate(path.cckNumber, ({ value }) =>
      !value() || NUMBER_ONLY_REGEX.test(value()) ? undefined : { kind: 'numeric-pattern' }
    );
    minLength(path.cckNumber, 8);
    maxLength(path.cckNumber, 8);
    debounce(path.name, 'blur');
    maxLength(path.name, 50);
  });
  myFiles: MyFile[] = [];

  columns: TableColumn[] = [];
  statusPopupElement: StatusPopupElement = closePopupData(
    createErrorPopupData('', '', this.translateService.instant('application.records.error.close-label'), 3000)
  );
  isActionPending = signal(false);
  areSortableColumnsMap = this.createAreSortableColumnsMap();
  sortColumnsMap = this.createSortColumnsMap();
  defaultSortedColumn = signal<ColumnSortEvent>({
    columnName: this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.creationDate),
    sortDirection: SortDirectionEnum.DESC
  });
  lastSortedColumn = signal<ColumnSortEvent | null>(null);
  currentSortProperty: string | undefined = undefined;
  currentSortDirection: SortDirectionEnum | undefined = undefined;
  hasBeenInitializedSeller = false;
  hasBeenInitializedIndex = false;
  openModifColumns = signal(false);
  userRecordsPreferences = signal<UserTablePreferences>(baseUserRecordsPreferences);

  isLoadingResetPreferences = signal(false);
  isLoadingChoosePreferences = signal(false);
  disableActionButton = signal(false);

  constructor() {
    effect(() => {
      const posValue = this.filterForm.pos().value();
      untracked(() => {
        let posToUse = posAll.value;
        if (this.haveAccessToSellerFilter() && posValue !== null) {
          if (posValue.key !== posAll.value) {
            posToUse =
              this.applicationStore
                .instant('userData')()
                ?.pointOfSales?.find(pointOfSale => pointOfSale.tvei === posValue.value)?.name ?? posAll.value;
          }
          if (!posToUse) {
            return;
          }
          this.fetchAvailableSellers(posToUse);
        }
      });
    });
    effect(() => {
      const sellerListMap = this.sellerListMap();
      const filesUpdate = this.files();
      const isSelectedSeller = this.selectedSeller() !== null;
      if (this.loadedFiles && filesUpdate) {
        for (const tableRow of this.loadedFiles) {
          this.mapSellerName(sellerListMap, tableRow, isSelectedSeller);
        }
      }
    });
    effect(() => {
      const subStatusList = this.referenceStore.referenceList().subStatus;
      if (this.files() && this.loadedFiles) {
        for (const tableRow of this.loadedFiles) {
          this.mapStatusLabel(subStatusList, tableRow);
        }
      }
    });
    effect(() => {
      const sortProperty = this.sortProperty();
      const sortDirection = this.sortDirection();
      if (sortProperty && sortDirection) {
        this.lastSortedColumn.set(this.getLastSortedColumn(sortProperty, sortDirection));
      }
    });
  }

  ngOnInit(): void {
    this.pageEvent.pageIndex = this.index() ?? 1;
    this.preferencesService
      .getUserRecordsPreferences()
      .pipe(
        tap(userTablePreferences => {
          if (userTablePreferences?.columnsOrder && userTablePreferences?.columnsDisplayed) {
            this.userRecordsPreferences.set(userTablePreferences);
            this.setDefaultAndLastSortedColumn(true, false);
          }
        }),
        catchError(() => of(null)),
        switchMap(() =>
          toObservable(this.filters, { injector: this.injector }).pipe(
            map(({ pos, seller, cckNumber, name, unreadMessage }) => ({ pos, seller, cckNumber, name, unreadMessage })),
            distinctUntilChanged(
              (a, b) =>
                areEqualsKeyValue(a.pos, b.pos) &&
                areEqualsKeyValue(a.seller, b.seller) &&
                a.cckNumber === b.cckNumber &&
                a.name === b.name &&
                a.unreadMessage === b.unreadMessage
            ),
            debounceTime(100),
            filter(
              ({ pos, seller }) =>
                this.filterForm().valid() && pos !== null && (!this.haveAccessToSellerFilter() || seller !== null)
            )
          )
        )
      )
      .subscribe(({ pos, seller, cckNumber, name, unreadMessage }) => {
        this.cdr.markForCheck();
        if (pos?.value && (!this.haveAccessToSellerFilter() || seller?.value)) {
          let isPageIndexed = false;
          if (!this.hasBeenInitializedIndex) {
            isPageIndexed = !!this.index();
            this.setCurrentSortProperty();
            this.setCurrentSortDirection();
          }
          this.hasBeenInitializedIndex = true;
          this.loadReferenceStoreAndFetchFiles(
            pos?.value,
            seller?.value,
            cckNumber,
            name,
            unreadMessage,
            isPageIndexed
          );
        }
        this.isActionPending.set(false);
      });
  }

  ngAfterViewInit(): void {
    initFiltersFormSignal(this.filters, this.pos(), this.cckNumber(), this.clientName(), this.tveiListMap);
  }

  protected onPageChanged(pageEvent: PageEvent): void {
    this.pageEvent.pageSize = pageEvent.pageSize;
    this.pageEvent.pageIndex = pageEvent.pageIndex;
    this.loadReferenceStoreAndFetchFiles(
      this.filterForm.pos().value()?.value,
      this.filterForm.seller().value()?.value,
      this.filterForm.cckNumber().value(),
      this.filterForm.name().value(),
      this.filterForm.unreadMessage().value(),
      true
    );
  }

  protected onCellClick(tableData: TableData): void {
    const file = this.myFiles.find(file => file.cckNumber === tableData.value);
    if (file) {
      this.applicationStore.update('actionsData', { cckNumber: file.cckNumber, actions: file.actions });
    }
    this.authenticationService
      .generateInternalToken(tableData.value)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(accessToken => {
          this.updateWorkListFilters();
          void this.router.navigate([`/contract-action-hub/${tableData.value}/resume`], {
            queryParams: { token: accessToken }
          });
        })
      )
      .subscribe();
  }

  protected searchWithCckNumber(): void {
    this.filterForm.cckNumber().markAsTouched();
    this.scrollToRecords();
  }

  protected searchWithClientName(): void {
    this.filterForm.name().markAsTouched();
    this.scrollToRecords();
  }

  protected goToAction(event: TableActionEvent): void {
    const cckNumber = this.getCckNumberAndUpdateActions(event.data);
    if (cckNumber) {
      iif(
        () => event.action !== ActionButtonEnum.DUPLICATION,
        this.authenticationService.generateInternalToken(cckNumber).pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(accessToken => {
            this.updateWorkListFilters();
            void this.router.navigate([`/contract-action-hub/${cckNumber}/${event.action}`], {
              queryParams: { token: accessToken }
            });
          })
        ),
        this.contractActionService.duplicateContract(cckNumber).pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(saveLeadResponse => {
            this.applicationStore.update('leadsFilters', null);
            void this.router.navigate(['/simulation'], {
              queryParams: { externalId: saveLeadResponse.reference, token: saveLeadResponse.accessToken }
            });
          })
        )
      )
        .pipe(
          catchError(() => {
            this.setErrorStatusPopup();
            return of(null);
          })
        )
        .subscribe();
    } else {
      this.setErrorStatusPopup();
    }
  }

  protected cancelContract(event: TableSubActionEvent): void {
    this.isActionPending.set(true);
    const cckNumber = this.getCckNumberAndUpdateActions(event.data);
    if (cckNumber) {
      this.contractActionService
        .cancelContractBySeller(cckNumber, event.subAction)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.isActionPending.set(false);
            this.setErrorStatusPopup();
            return EMPTY;
          }),
          switchMap(() =>
            this.fetchFiles(
              this.filterForm.pos().value()?.value,
              this.filterForm.seller().value()?.value,
              this.filterForm.cckNumber().value(),
              this.filterForm.name().value(),
              this.filterForm.unreadMessage().value(),
              true
            ).pipe(
              tap(data => {
                this.files.set(data);
                this.isActionPending.set(false);
                this.setCancellationSuccessStatusPopup();
              })
            )
          )
        )
        .subscribe();
    } else {
      this.isActionPending.set(false);
      this.setErrorStatusPopup();
    }
  }

  protected sortColumn(event: ColumnSortEvent): void {
    this.isActionPending.set(true);
    if (event.columnName) {
      this.currentSortProperty = event.sortDirection ? this.sortColumnsMap.get(event.columnName) : undefined;
      this.currentSortDirection = event.sortDirection ?? undefined;
      this.fetchFiles(
        this.filterForm.pos().value()?.value,
        this.filterForm.seller().value()?.value,
        this.filterForm.cckNumber().value(),
        this.filterForm.name().value(),
        this.filterForm.unreadMessage().value(),
        false
      ).subscribe({
        next: data => {
          this.files.set(data);
          this.isActionPending.set(false);
        },
        error: () => {
          this.setErrorStatusPopup();
          this.isActionPending.set(false);
        }
      });
    } else {
      this.setErrorStatusPopup();
      this.isActionPending.set(false);
    }
  }

  protected closeStatusMessagePopup(): void {
    this.statusPopupElement = closePopupData(this.statusPopupElement);
  }

  protected resetFilters(): void {
    const isPosModified = this.filters().pos.key !== this.tveiListMap[0].key;
    this.filters.set({
      pos: this.tveiListMap[0],
      seller: this.sellerListMap()[0],
      cckNumber: '',
      name: '',
      unreadMessage: false
    });
    if (isPosModified) {
      this.selectedSeller.set(null);
    }
    this.pageEvent.pageIndex = 1;
    this.files.set(undefined);
    this.loadedFiles = null;
  }

  protected onKeyDownResetFilters($event: KeyboardEvent): void {
    if ($event.key === 'Enter' || $event.code === 'Space') {
      this.resetFilters();
      $event.preventDefault();
    }
  }

  protected hasAtLeastOneFilter(): boolean {
    return (
      this.filterForm.pos().value()?.key !== posAll.value ||
      this.filterForm.seller().value()?.key !== sellerAll.value ||
      !!this.filterForm.cckNumber().value() ||
      !!this.filterForm.name().value() ||
      this.filterForm.unreadMessage().value()
    );
  }

  protected haveAccessToSellerFilter(): boolean {
    return isUserPosWorklistRightPvmo(this.applicationStore.instant('userData')()?.userRights);
  }

  protected getColumns(): TableColumn[] {
    const widthMap: Record<string, number> = {
      [this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.creationDate)]: 10.5,
      [this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.cckNumber)]: 8,
      [this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.prospectName)]: 12,
      [this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.siclidProductCode)]: 12,
      [this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.insurances)]: 11,
      [this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.statusSigned)]: 8,
      [this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.subStatus)]: 10,
      [this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.rvDerogationStatus)]: 10.5
    };

    return getColumnsWithWantedWidths(this.columns, widthMap);
  }

  protected saveRecordsPreferences(): void {
    this.isLoadingChoosePreferences.set(true);
    this.preferencesService.recordsPreferencesValidSubject
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        distinctUntilChanged(),
        switchMap(() =>
          defer(() => {
            const userTablePreferences = this.applicationStore.instant('recordsPreferences')();
            if (userTablePreferences) {
              return this.updatePreferencesAndFetchFiles(userTablePreferences as UserTablePreferences, false);
            } else {
              this.setErrorStatusPopup();
              return EMPTY;
            }
          }).pipe(
            finalize(() => {
              this.openModifColumns.set(false);
              this.isLoadingChoosePreferences.set(false);
            })
          )
        )
      )
      .subscribe();
  }

  protected resetColumnsPreferences(): void {
    this.isLoadingResetPreferences.set(true);
    this.updatePreferencesAndFetchFiles(baseUserRecordsPreferences, true)
      .pipe(
        finalize(() => {
          this.isLoadingResetPreferences.set(false);
        })
      )
      .subscribe();
  }

  protected openPopinPreferences(): void {
    this.openModifColumns.set(true);
    this.disableActionButton.set(false);
  }

  private setCurrentSortProperty(): void {
    if (this.sortProperty()) {
      this.currentSortProperty = this.sortProperty();
    } else if (!this.currentSortProperty) {
      const key = this.defaultSortedColumn()?.columnName;
      this.currentSortProperty = key ? this.sortColumnsMap.get(key) : undefined;
    }
  }

  private setCurrentSortDirection(): void {
    if (this.sortDirection()) {
      this.currentSortDirection = this.sortDirection();
    } else if (!this.currentSortDirection) {
      this.currentSortDirection = this.defaultSortedColumn()?.sortDirection ?? undefined;
    }
  }

  private setDefaultAndLastSortedColumn(isInit: boolean, isReset: boolean): void {
    const columnsSorted = [...this.userRecordsPreferences().columnsDisplayed].sort(
      (a, b) => this.userRecordsPreferences().columnsOrder[a] - this.userRecordsPreferences().columnsOrder[b]
    );
    let columnToSort: string | null = null;
    for (const column of columnsSorted) {
      if (
        Array.from(this.areSortableColumnsMap.keys()).includes(
          this.translateService.instant(prefixRouteRecordsColumns + column)
        )
      ) {
        columnToSort = column;
        break;
      }
    }
    const value = {
      columnName: this.translateService.instant(
        prefixRouteRecordsColumns + (columnToSort ?? RecordsColumnsEnum.creationDate)
      ),
      sortDirection: SortDirectionEnum.DESC
    };
    this.defaultSortedColumn.set(value);
    if (isReset || (isInit ? !this.sortProperty() : !this.isSortPropertyValid())) {
      this.lastSortedColumn.set(value);
    }
  }

  private updatePreferencesAndFetchFiles(
    userTablePreferences: UserTablePreferences,
    isReset: boolean
  ): Observable<TableRow[]> {
    return this.preferencesService.updateUserRecordsPreferences(userTablePreferences).pipe(
      tap(() => {
        this.userRecordsPreferences.set(userTablePreferences);
        this.setDefaultAndLastSortedColumn(false, isReset);
      }),
      switchMap(() => {
        this.files.set(undefined);
        const sortPropertyValid = this.isSortPropertyValid();
        const defaultKey = this.defaultSortedColumn().columnName;
        const newProperty = defaultKey ? this.sortColumnsMap.get(defaultKey) : undefined;
        if (!sortPropertyValid || isReset) {
          this.currentSortProperty = newProperty;
          this.currentSortDirection = this.defaultSortedColumn().sortDirection ?? undefined;
        }
        return this.fetchFiles(
          this.filterForm.pos().value()?.value,
          this.filterForm.seller()?.value()?.value,
          this.filterForm.cckNumber().value(),
          this.filterForm.name().value(),
          this.filterForm.unreadMessage().value(),
          sortPropertyValid && !isReset
        );
      }),
      tap((tableRows: TableRow[]) => this.files.set(tableRows)),
      catchError(() => {
        this.setErrorStatusPopup();
        return EMPTY;
      })
    );
  }

  private isSortPropertyValid(): boolean {
    const property = Array.from(this.sortColumnsMap.entries()).find(
      ([, value]) => value === this.currentSortProperty
    )?.[0];
    return this.userRecordsPreferences().columnsDisplayed.some(
      column => this.translateService.instant(prefixRouteRecordsColumns + column) === property
    );
  }

  private getCckNumberAndUpdateActions(data: TableRow): string | undefined {
    const cckNumber = data.tableData.find(
      x => x.key === this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.cckNumber)
    )?.value;
    const file = this.myFiles.find(file => file.cckNumber === cckNumber);
    if (file) {
      this.applicationStore.update('actionsData', { cckNumber: file.cckNumber, actions: file.actions });
    }
    return cckNumber;
  }

  private setCancellationSuccessStatusPopup(): void {
    this.statusPopupElement = createSuccessPopupData(
      this.translateService.instant('application.records.cancellation.success.title'),
      this.translateService.instant('application.records.cancellation.success.message'),
      this.translateService.instant('application.records.cancellation.success.close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }

  private setErrorStatusPopup(): void {
    this.statusPopupElement = createErrorPopupData(
      this.translateService.instant('application.records.error.title'),
      this.translateService.instant('application.records.error.message'),
      this.translateService.instant('application.records.error.close-label'),
      undefined
    );
    this.cdr.markForCheck();
  }

  private fetchFiles(
    posValue: string | NoValue,
    sellerValue: string | NoValue,
    cckNumber: string | NoValue,
    name: string | NoValue,
    unread: boolean | NoValue,
    pageIndex: boolean
  ): Observable<TableRow[]> {
    const sellerUserLogin = sellerValue === sellerAll.value ? null : sellerValue;
    const body: RecordsRequest = {
      cckNumbers: cckNumber ? [cckNumber] : undefined,
      names: name ? [name] : undefined,
      unreadMessage: unread || undefined,
      partnerInfo: {
        webStoreIds: getWebStoreIds(posValue, this.tveiListMap)
      },
      userLogin: this.haveAccessToSellerFilter()
        ? sellerUserLogin
        : this.applicationStore.instant('userData')()?.jossoLogin,
      caller: 'F'
    };
    return this.recordsService
      .getFilesList(
        body,
        pageIndex ? this.pageEvent.pageIndex - 1 : 0,
        this.pageEvent.pageSize,
        this.currentSortProperty,
        this.currentSortDirection
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((recordsResponse: RecordsResponse) => {
          if (recordsResponse) {
            this.pageEvent = {
              length: recordsResponse.numberOfElements,
              pageSize: recordsResponse.size,
              pageIndex: recordsResponse.page + 1
            };
            this.myFiles = recordsResponse.myFileResponses;
            return recordsResponse.myFileResponses.map(file =>
              rowDataToTableRow(
                fileToRowData(file, this.translateService, this.locale),
                this.translateService,
                this.userRecordsPreferences()
              )
            );
          } else {
            this.pageEvent.length = 0;
            return [];
          }
        }),
        tap(results => {
          this.loadedFiles = results;
          const columnsTable = getTableColumnsFromTableRows(this.loadedFiles);
          if (columnsTable.length > 0) {
            if (this.userRecordsPreferences()?.columnsDisplayed.includes(RecordsColumnsEnum.unreadMessages)) {
              const preferencesListSorted = [...this.userRecordsPreferences().columnsDisplayed].sort(
                (a, b) => this.userRecordsPreferences().columnsOrder[a] - this.userRecordsPreferences().columnsOrder[b]
              );
              const index = preferencesListSorted.indexOf(RecordsColumnsEnum.unreadMessages);
              columnsTable.splice(index, 0, {
                name: RecordsColumnsEnum.unreadMessages,
                width: 5
              });
            }
            columnsTable.push({ name: RecordsColumnsEnum.actions, width: 4.5 });
            this.columns = columnsTable;
          }
          this.cdr.markForCheck();
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  private scrollToRecords(): void {
    this.records().nativeElement.scrollIntoView({ behavior: 'smooth' });
    this.records().nativeElement.focus();
  }

  private loadReferenceStoreAndFetchFiles(
    posValue: string | NoValue,
    sellerValue: string | NoValue,
    cckNumber: string | NoValue,
    name: string | NoValue,
    unread: boolean | NoValue,
    pageIndex: boolean
  ): void {
    if (!this.referenceStore.isStoreFull()) {
      this.referenceService.fetchAllReferences().subscribe();
    }
    this.files.set(undefined);
    this.fetchFiles(posValue, sellerValue, cckNumber, name, unread, pageIndex).subscribe(data => {
      this.files.set(data);
    });
  }

  private createSortColumnsMap(): Map<string, string> {
    const sortColumnsMap = new Map<string, string>();
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.creationDate),
      RecordsSortProperty.CREATION_DATE
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.cckNumber),
      RecordsSortProperty.CCK_NUMBER
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.prospectName),
      RecordsSortProperty.PROSPECT_NAME
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.siclidProductCode),
      RecordsSortProperty.SICLID_PRODUCT_CODE
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.insurances),
      RecordsSortProperty.INSURANCES
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.statusSigned),
      RecordsSortProperty.STATUS_SIGNED
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.subStatus),
      RecordsSortProperty.SUB_STATUS
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.rvDerogationStatus),
      RecordsSortProperty.RV_DEROGATION_STATUS
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.webStoreId),
      RecordsSortProperty.WEBSTORE_ID
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.userLogin),
      RecordsSortProperty.USER_LOGIN
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.overdraftAmount),
      RecordsSortProperty.OVERDRAFT_AMOUNT
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.model),
      RecordsSortProperty.MODEL
    );
    sortColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.used),
      RecordsSortProperty.USED
    );
    return sortColumnsMap;
  }

  private createAreSortableColumnsMap(): Map<string, boolean> {
    const areSortableColumnsMap = new Map<string, boolean>();
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.creationDate),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.cckNumber),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.prospectName),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.siclidProductCode),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.insurances),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.statusSigned),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.subStatus),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.rvDerogationStatus),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.webStoreId),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.userLogin),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.overdraftAmount),
      true
    );
    areSortableColumnsMap.set(
      this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.model),
      true
    );
    areSortableColumnsMap.set(this.translateService.instant(prefixRouteRecordsColumns + RecordsColumnsEnum.used), true);
    return areSortableColumnsMap;
  }

  private updateWorkListFilters(): void {
    this.applicationStore.update('workListFilters', {
      index: this.pageEvent.pageIndex,
      pos: this.filterForm.pos().value()?.value,
      seller: this.filterForm.seller().value()?.value,
      cckNumber: this.filterForm.cckNumber().value(),
      clientName: this.filterForm.name().value(),
      sortDirection: this.currentSortDirection,
      sortProperty: this.currentSortProperty
    });
  }

  private getLastSortedColumn(sortProperty: string, sortDirection: SortDirectionEnum): ColumnSortEvent | null {
    const columnName = Array.from(this.sortColumnsMap.entries()).find(([, value]) => value === sortProperty)?.[0];
    if (columnName && sortDirection) {
      return {
        columnName: columnName,
        sortDirection: sortDirection
      };
    }
    return null;
  }

  private mapSellerName(sellerListMap: KeyValueLabel[], tableRow: TableRow, isSelectedSeller: boolean): void {
    if (
      untracked(() => this.userRecordsPreferences()?.columnsDisplayed.includes(RecordsColumnsEnum.userLogin)) &&
      sellerListMap
    ) {
      const tableData = this.getTableDataByColumnName(tableRow, RecordsColumnsEnum.userLogin);
      if (tableData) {
        const userLogin = tableData.value;
        tableData.value = sellerListMap.find(seller => seller.key === userLogin)?.label ?? userLogin;
        tableData.isLoading = !isSelectedSeller;
      }
    }
  }

  private mapStatusLabel(subStatusList: ReferenceValue[], tableRow: TableRow): void {
    if (
      untracked(() => this.userRecordsPreferences()?.columnsDisplayed.includes(RecordsColumnsEnum.subStatus)) &&
      subStatusList
    ) {
      const tableData = this.getTableDataByColumnName(tableRow, RecordsColumnsEnum.subStatus);
      if (tableData) {
        const subStatus = tableData.value;
        tableData.value = getReferenceValue(subStatus, subStatusList);
        if (Number.isNaN(Number(tableData.value))) {
          tableData.isLoading = false;
        }
      }
    }
  }

  private getTableDataByColumnName(tableRow: TableRow, recordsColumnsEnum: RecordsColumnsEnum): TableData | undefined {
    return tableRow.tableData.find(
      data => data.key === this.translateService.instant(prefixRouteRecordsColumns + recordsColumnsEnum)
    );
  }

  private fetchAvailableSellers(posToUse: string): void {
    this.userService
      .getAvailableSellers({
        tveiNames: posToUse === posAll.value ? null : [posToUse],
        isExternal: this.applicationStore.instant('userData')()?.isExternal ? true : null
      })
      .subscribe(sellerList => {
        this.sellerListMap.set(sellersToKeyValueLabelsWithAllOption(sellerList, this.translateService));
        if (!this.hasBeenInitializedSeller && this.seller()) {
          const querySeller = this.sellerListMap().find(seller => seller.value === this.seller());
          this.filterForm.seller().value.set(querySeller ?? this.sellerListMap()[0]);
          this.selectedSeller.set(querySeller ?? this.sellerListMap()[0]);
          this.hasBeenInitializedSeller = true;
        } else if (!this.seller()) {
          this.filterForm.seller().value.set(this.sellerListMap()[0]);
          this.selectedSeller.set(this.sellerListMap()[0]);
        }
      });
  }
}
